import { Big } from 'big.js';
import { match } from 'ts-pattern';
import { DisplayData, TenantJobLine } from '~/lib/model';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import BreJob from '~/tenants/bre/model/BreJob';
import { BreOrderType } from '~/tenants/bre/model/enums';
import { CONTENT_WRITING_PAY, CONTENT_WRITING_RATE } from '~/tenants/bre/performable/common';
import BreBrochuresConfig, { BrochurePaper } from '~/tenants/bre/performable/print/brochures/BreBrochuresConfig';
import { BreBrochuresPay, BreBrochuresRate } from '~/tenants/bre/performable/print/brochures/BreBrochuresData';
import { BreDesignPay, BreDesignRate } from '~/tenants/bre/performable/print/design/BreDesignData';
import { BreProviderId } from '~/tenants/bre/model/BreProvider';
import BrePhotosConfig from '~/tenants/bre/performable/photos/BrePhotosConfig';
import { datetime } from '~/lib/datettime';

interface PaperTypeOptions {
  paper: BrochurePaper;
}

export default class BreBrochuresJob extends BreJob<typeof BreBrochuresConfig> {
  get performable(): typeof BreBrochuresConfig {
    return BreBrochuresConfig;
  }

  get configurable(): boolean {
    return true;
  }

  get eligible(): boolean {
    return this.isOneOf(BreOrderType.RESIDENTIAL, BreOrderType.COMMERCIAL, BreOrderType.PROPERTY_MKTG);
  }

  get dependencies(): string[] {
    return [BrePhotosConfig.id];
  }

  get assignee(): string | null {
    return BreProviderId.PRINT_TEAM.toString();
  }

  info(): DisplayData[] {
    const info = super.info();

    info.push({
      name: 'Sides',
      value: this.metadata.sides,
      provider: true,
    });

    if (this.metadata.no_free_uv) {
      info.push({
        name: 'UV Opt-out',
        value: 'Yes',
        provider: true
      });
    }

    info.push({
      name: 'Count',
      value: this.metadata.count,
      provider: true,
    });

    info.push({
      name: 'Paper',
      value: this.paperType,
      provider: true,
    });

    info.push({
      name: 'Content Writing',
      value: this.metadata.content_writing ? 'Yes' : 'No',
      provider: true,
    });

    if (this.metadata.design) {
      info.push({
        name: 'Design',
        value: this.metadata.design,
        provider: true,
      });
    }

    info.push({
      name: 'Need By',
      value: datetime(this.metadata.need_by, this.order.location.timezone).toFormat('LLL dd, yyyy'),
      provider: true,
    });

    return info;
  }

  get paperType(): string {
    const { paper } = this.metadata;
    return BreBrochuresJob.paperType({ paper });
  }

  static paperType({ paper }: PaperTypeOptions): string {
    return match(paper)
      .with('low_gloss', () => 'Heavy-gloss outside (free uv), low-gloss inside')
      .with('heavy_gloss', () => 'Heavy-gloss outside (free uv), heavy-gloss inside')
      .exhaustive();
  }

  isDelivery(): boolean {
    return true;
  }

  isPrint(): boolean {
    return true;
  }

  defaultValue(): ZodVersionedMetadata<(typeof BreBrochuresConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
      sides: '8',
      count: '50',
      paper: 'low_gloss',
      need_by: datetime(this.order.created, this.order.timezone).toSQLDate()
    };
  }

  revenueLines(): TenantJobLine[] {
    const { count, paper, sides, content_writing, design } = this.metadata;
    const rate = BreBrochuresRate[sides]?.[paper]?.[count];
    const lines: TenantJobLine[] = [];

    if (rate) {
      lines.push({
        id: 'brochures_print',
        description: `${sides} Pages ${this.paperType} (x${count})`,
        amount: new Big(rate),
        taxable: true,
      });
    }

    if (content_writing) {
      lines.push({
        id: 'content_writing',
        description: 'Content Writing',
        amount: new Big(CONTENT_WRITING_RATE),
      });
    }

    if (design) {
      const rate = BreDesignRate[design][`brochure_${sides}`];

      if (rate) {
        lines.push({
          id: 'brochure_design',
          description: `Brochure design (${design} layout)`,
          amount: new Big(rate),
        });
      }
    }

    return lines;
  }

  expenseLines(): TenantJobLine[] {
    const { count, paper, sides, content_writing, design } = this.metadata;
    const pay = BreBrochuresPay[sides]?.[paper]?.[count];
    const lines: TenantJobLine[] = [];

    if (pay) {
      lines.push({
        id: 'brochures_print',
        description: `${sides} Pages ${this.paperType} (x${count})`,
        amount: new Big(pay),
      });
    }

    if (content_writing) {
      lines.push({
        id: 'content_writing',
        description: 'Content Writing',
        amount: new Big(CONTENT_WRITING_PAY),
      });
    }

    if (design) {
      const pay = BreDesignPay[design][`brochure_${sides}`];

      if (pay) {
        lines.push({
          id: 'brochure_design',
          description: `Brochure design (${design} layout)`,
          amount: new Big(pay),
        });
      }
    }

    return lines;
  }
}
